/*
  Product gallery
*/
if (!customElements.get('product-gallery')) {
  class ProductGallery extends HTMLElement {
    connectedCallback () {
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'))
      this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'))
      this.previousButton = this.querySelector('[data-gallery-previous]')
      this.nextButton = this.querySelector('[data-gallery-next]')
      this.galleryList = this.querySelector('.product-gallery-list')
      this.activeIndex = Math.max(this.items.findIndex((item) => item.classList.contains('is-active')), 0)
      this.videos = Array.from(this.querySelectorAll('.product-gallery-item video.product-gallery-media'))
      this.touchStartX = null
      this.touchStartY = null
      this.lastSwipeAt = 0

      this.onPreviousClick = () => this.showItem(this.activeIndex - 1)
      this.onNextClick = () => this.showItem(this.activeIndex + 1)
      this.onVariantChange = this.handleVariantChange.bind(this)
      this.onVideoTimeUpdate = (event) => this.updateVideoProgress(event.currentTarget)
      this.onVideoClick = (event) => this.toggleVideo(event.currentTarget)
      this.onVideoStateChange = (event) => this.updateVideoState(event.currentTarget)
      this.onTouchStart = this.handleTouchStart.bind(this)
      this.onTouchEnd = this.handleTouchEnd.bind(this)

      this.previousButton?.addEventListener('click', this.onPreviousClick)
      this.nextButton?.addEventListener('click', this.onNextClick)
      this.galleryList?.addEventListener('touchstart', this.onTouchStart, { passive: true })
      this.galleryList?.addEventListener('touchend', this.onTouchEnd, { passive: true })
      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => this.showItem(index))
      })
      this.videos.forEach((video) => {
        video.addEventListener('click', this.onVideoClick)
        video.addEventListener('play', this.onVideoStateChange)
        video.addEventListener('pause', this.onVideoStateChange)
        video.addEventListener('timeupdate', this.onVideoTimeUpdate)
        video.addEventListener('loadedmetadata', this.onVideoTimeUpdate)
        this.updateVideoState(video)
      })
      document.addEventListener('product:variant-change', this.onVariantChange)
      this.showItem(this.activeIndex)
    }

    disconnectedCallback () {
      this.previousButton?.removeEventListener('click', this.onPreviousClick)
      this.nextButton?.removeEventListener('click', this.onNextClick)
      this.galleryList?.removeEventListener('touchstart', this.onTouchStart)
      this.galleryList?.removeEventListener('touchend', this.onTouchEnd)
      this.videos.forEach((video) => {
        video.removeEventListener('click', this.onVideoClick)
        video.removeEventListener('play', this.onVideoStateChange)
        video.removeEventListener('pause', this.onVideoStateChange)
        video.removeEventListener('timeupdate', this.onVideoTimeUpdate)
        video.removeEventListener('loadedmetadata', this.onVideoTimeUpdate)
      })
      document.removeEventListener('product:variant-change', this.onVariantChange)
    }

    toggleVideo (video) {
      if (Date.now() - this.lastSwipeAt < 500) return

      if (video.paused) {
        const playPromise = video.play()

        playPromise?.catch(() => {})
      } else {
        video.pause()
      }
    }

    handleTouchStart (event) {
      if (!window.matchMedia('(max-width: 599px)').matches || event.touches.length !== 1) return

      this.touchStartX = event.touches[0].clientX
      this.touchStartY = event.touches[0].clientY
    }

    handleTouchEnd (event) {
      if (this.touchStartX === null || this.touchStartY === null) return

      const horizontalDistance = event.changedTouches[0].clientX - this.touchStartX
      const verticalDistance = event.changedTouches[0].clientY - this.touchStartY

      this.touchStartX = null
      this.touchStartY = null

      if (Math.abs(horizontalDistance) < 40 || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return

      this.lastSwipeAt = Date.now()
      this.showItem(horizontalDistance < 0 ? this.activeIndex + 1 : this.activeIndex - 1)
    }

    updateVideoState (video) {
      video.classList.toggle('is-playing', !video.paused)
    }

    updateVideoProgress (video) {
      const progress = video.closest('[data-gallery-item]')?.querySelector('[data-gallery-video-progress]')
      const percentage = video.duration ? video.currentTime / video.duration : 0

      progress?.style.setProperty('--product-gallery-video-progress', String(percentage))
    }

    handleVariantChange (event) {
      const mediaId = event.detail?.featuredMediaId

      if (!mediaId) return

      const index = this.items.findIndex((item) => item.dataset.mediaId === String(mediaId))

      if (index >= 0) this.showItem(index)
    }

    showItem (index) {
      if (!this.items.length) return

      const nextIndex = ((index % this.items.length) + this.items.length) % this.items.length

      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === nextIndex

        item.classList.toggle('is-active', isActive)
        item.setAttribute('aria-hidden', String(!isActive))
        item.toggleAttribute('inert', !isActive)
        item.querySelectorAll('video').forEach((video) => {
          if (isActive) {
            const playPromise = video.play()

            playPromise?.catch(() => {})
          } else {
            video.pause()
            video.currentTime = 0
          }
        })
      })

      this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === nextIndex

        thumbnail.classList.toggle('is-active', isActive)
        thumbnail.toggleAttribute('aria-current', isActive)
      })

      this.activeIndex = nextIndex
    }
  }

  customElements.define('product-gallery', ProductGallery)
}

/*
  Product options
*/
if (!customElements.get('product-options')) {
  class ProductOptions extends HTMLElement {
    connectedCallback () {
      this.sectionId = this.dataset.sectionId
      this.inputs = Array.from(this.querySelectorAll('[data-product-option-value]'))
      this.variants = this.parseJson('[data-product-option-variants]')
      this.onOptionChange = this.updateSelectedVariant.bind(this)

      this.inputs.forEach((input) => input.addEventListener('change', this.onOptionChange))
    }

    disconnectedCallback () {
      this.inputs.forEach((input) => input.removeEventListener('change', this.onOptionChange))
    }

    parseJson (selector) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '[]')
      } catch (error) {
        console.error('Unable to parse product variants', error)
        return []
      }
    }

    updateSelectedVariant () {
      const selectedOptions = this.inputs
        .filter((input) => input.checked)
        .sort((firstInput, secondInput) => Number(firstInput.dataset.optionPosition) - Number(secondInput.dataset.optionPosition))
        .map((input) => input.value)
      const variant = this.variants.find((item) => {
        return item.options.every((option, index) => option === selectedOptions[index])
      })

      if (!variant) return

      this.dataset.currentVariantId = String(variant.id)

      const url = new URL(window.location.href)
      url.searchParams.set('variant', variant.id)
      window.history.replaceState({}, '', url)

      document.dispatchEvent(new CustomEvent('product:variant-change', {
        detail: {
          sectionId: this.sectionId,
          variantId: variant.id,
          available: variant.available,
          featuredMediaId: variant.featuredMediaId
        }
      }))
    }
  }

  customElements.define('product-options', ProductOptions)
}

/*
  Product price
*/
if (!customElements.get('product-price')) {
  class ProductPrice extends HTMLElement {
    connectedCallback () {
      this.sectionId = this.dataset.sectionId
      this.values = this.querySelector('.product-price-values')
      this.priceElements = Array.from(this.querySelectorAll('[data-product-price]'))
      this.comparePrice = this.querySelector('[data-product-compare-price]')
      this.saleBadge = this.querySelector('[data-product-sale-badge]')
      this.paymentTermsVariantInput = this.querySelector('[data-product-payment-terms-variant-input]')
      this.variants = new Map(this.parseVariants().map((variant) => [String(variant.id), variant]))
      this.onVariantChange = this.handleVariantChange.bind(this)

      document.addEventListener('product:variant-change', this.onVariantChange)
    }

    disconnectedCallback () {
      document.removeEventListener('product:variant-change', this.onVariantChange)
    }

    parseVariants () {
      try {
        return JSON.parse(this.querySelector('[data-product-price-variants]')?.textContent || '[]')
      } catch (error) {
        console.error('Unable to parse product prices', error)
        return []
      }
    }

    handleVariantChange (event) {
      if (event.detail?.sectionId !== this.sectionId && this.dataset.syncVariants !== 'true') return

      const variant = this.variants.get(String(event.detail.variantId))

      if (!variant || !this.values) return

      this.values.dataset.hasComparePrice = String(variant.hasComparePrice)
      this.values.dataset.available = String(variant.available)
      this.priceElements.forEach((element) => { element.textContent = variant.price })

      if (this.comparePrice) this.comparePrice.textContent = variant.compareAtPrice || ''
      if (this.saleBadge) this.saleBadge.textContent = variant.saleBadge || ''
      if (this.paymentTermsVariantInput) {
        this.paymentTermsVariantInput.value = variant.id
        this.paymentTermsVariantInput.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }
  }

  customElements.define('product-price', ProductPrice)
}

/*
  Product buy buttons
*/
if (!customElements.get('product-buy-buttons')) {
  class ProductBuyButtons extends HTMLElement {
    connectedCallback () {
      this.sectionId = this.dataset.sectionId
      this.variantInput = this.querySelector('[data-product-variant-input]')
      this.quantityInput = this.querySelector('[data-quantity-input]')
      this.decreaseButton = this.querySelector('[data-quantity-decrease]')
      this.increaseButton = this.querySelector('[data-quantity-increase]')
      this.submitButton = this.querySelector('[data-add-to-cart-button]')
      this.buyNowButton = this.querySelector('[data-buy-now-button]')
      this.submitText = this.querySelector('[data-add-to-cart-text]')
      this.variants = new Map(this.parseVariants().map((variant) => [String(variant.id), variant]))
      this.onVariantChange = this.handleVariantChange.bind(this)
      this.onProductQuantityChange = this.handleProductQuantityChange.bind(this)
      this.onDecrease = () => this.changeQuantity(-1)
      this.onIncrease = () => this.changeQuantity(1)
      this.onQuantityInput = this.handleQuantityInput.bind(this)
      this.onQuantityChange = this.normalizeQuantity.bind(this)
      this.onBuyNow = this.buyNow.bind(this)

      document.addEventListener('product:variant-change', this.onVariantChange)
      document.addEventListener('product:quantity-change', this.onProductQuantityChange)
      this.decreaseButton?.addEventListener('click', this.onDecrease)
      this.increaseButton?.addEventListener('click', this.onIncrease)
      this.quantityInput?.addEventListener('input', this.onQuantityInput)
      this.quantityInput?.addEventListener('change', this.onQuantityChange)
      this.buyNowButton?.addEventListener('click', this.onBuyNow)
      this.normalizeQuantity()
    }

    disconnectedCallback () {
      document.removeEventListener('product:variant-change', this.onVariantChange)
      document.removeEventListener('product:quantity-change', this.onProductQuantityChange)
      this.decreaseButton?.removeEventListener('click', this.onDecrease)
      this.increaseButton?.removeEventListener('click', this.onIncrease)
      this.quantityInput?.removeEventListener('input', this.onQuantityInput)
      this.quantityInput?.removeEventListener('change', this.onQuantityChange)
      this.buyNowButton?.removeEventListener('click', this.onBuyNow)
    }

    parseVariants () {
      try {
        return JSON.parse(this.querySelector('[data-product-buy-button-variants]')?.textContent || '[]')
      } catch (error) {
        console.error('Unable to parse buy button variants', error)
        return []
      }
    }

    handleVariantChange (event) {
      if (event.detail?.sectionId !== this.sectionId && this.dataset.syncVariants !== 'true') return

      const variant = this.variants.get(String(event.detail.variantId))

      if (!variant || !this.submitButton || !this.submitText) return

      this.variantInput.value = variant.id
      this.dataset.currentVariantId = String(variant.id)
      this.dataset.selectedMediaId = variant.featuredMediaId ? String(variant.featuredMediaId) : ''
      this.submitButton.disabled = !variant.available
      if (this.buyNowButton) this.buyNowButton.disabled = !variant.available
      this.updateSubmitText()
    }

    handleProductQuantityChange (event) {
      if (this.dataset.syncQuantity !== 'true' || !this.quantityInput) return

      const quantity = Math.max(Math.floor(Number(event.detail?.quantity)) || 1, 1)

      this.quantityInput.value = String(quantity)
      this.updateSubmitText()
    }

    handleQuantityInput () {
      this.updateSubmitText()
      this.dispatchQuantityChange()
    }

    changeQuantity (amount) {
      if (!this.quantityInput) return

      this.quantityInput.value = Math.max(Number(this.quantityInput.value) + amount, 1)
      this.normalizeQuantity()
    }

    normalizeQuantity () {
      if (!this.quantityInput) return

      const quantity = Math.max(Math.floor(Number(this.quantityInput.value)) || 1, 1)

      this.quantityInput.value = String(quantity)
      if (this.decreaseButton) this.decreaseButton.disabled = quantity <= 1
      this.updateSubmitText()
      this.dispatchQuantityChange()
    }

    dispatchQuantityChange () {
      if (!this.quantityInput || this.dataset.syncQuantity === 'true') return

      document.dispatchEvent(new CustomEvent('product:quantity-change', {
        detail: {
          sectionId: this.sectionId,
          quantity: Math.max(Math.floor(Number(this.quantityInput.value)) || 1, 1)
        }
      }))
    }

    updateSubmitText () {
      if (!this.submitText || !this.submitButton) return

      if (this.submitButton.disabled) {
        this.submitText.textContent = this.dataset.soldOutText
        return
      }

      if (this.dataset.isFreeProduct === 'true') {
        this.submitText.textContent = this.dataset.addToCartText
        return
      }

      const quantity = Math.max(Math.floor(Number(this.quantityInput?.value)) || 1, 1)
      const licenseText = quantity === 1 ? this.dataset.licenseText : this.dataset.licensesText

      this.submitText.textContent = `${this.dataset.addToCartText} - ${quantity} ${licenseText}`
    }

    buyNow () {
      if (!this.buyNowButton || this.buyNowButton.disabled || !this.variantInput?.value) return

      const quantity = Math.max(Math.floor(Number(this.quantityInput?.value)) || 1, 1)
      const cartUrl = this.dataset.cartUrl || '/cart'
      const permalink = `${cartUrl}/${encodeURIComponent(this.variantInput.value)}:${quantity}`

      this.buyNowButton.disabled = true
      this.buyNowButton.setAttribute('aria-busy', 'true')
      window.location.assign(permalink)
    }
  }

  customElements.define('product-buy-buttons', ProductBuyButtons)
}
